/*
 * assets/js/src/lightbox.js — zvětšená fotka a přehrávač videa v překryvu.
 *
 * Nahrazuje legacy `jquery.fancybox.js` (22 016 B přenosu) na detailu
 * programu. Odpojení legacy se nedělá tady, ale tím, že šablona nevypíše
 * `data-fancybox` — modul jen obsluhuje to, co vypíše.
 *
 * ---------------------------------------------------------------------
 * CO MODUL NEDĚLÁ
 * ---------------------------------------------------------------------
 * 1. **Nerozhoduje, co je to za video.** Návrh v inline skriptu parsoval
 *    `?v=` z adresy, tedy uměl jen YouTube. Na webu je ale 24 YouTube
 *    a 21 Vimeo videí (změřeno 17. 9. přes všech 82 programů), takže by
 *    to polovině programů nefungovalo. Hotovou adresu přehrávače skládá
 *    PHP a modul ji čte z `data-za-embed`.
 * 2. **Nenese žádný text.** Popisky tlačítek jsou v markupu z registru
 *    textů; kdyby byly tady, editor by je neuviděl a EN mutace by zůstala
 *    česky. Počítadlo skládá jen čísla a lomítko.
 * 3. **Neobaluje cizí obrázky.** Návrh dogeneroval `<button>` kolem
 *    `.gal .ggrid > img`. U nás je prvek `<a href>` už v HTML, protože
 *    bez skriptu musí fotka zůstat otevíratelná — dogenerovat prvek
 *    skriptem by znamenalo, že bez skriptu není.
 *
 * Bez skriptu: odkazy vedou na plnou fotku a na video, překryv zůstane
 * skrytý (`.lbox` má `display: none`, dokud nemá třídu `on`).
 */

const ROOT = '#lbox';
/*
 * FOTKY: `.zoomable` je prvek galerie z návrhu; `[data-za-lightbox]`
 * přibylo 17. 9., když se překryv rozšířil mimo detail programu —
 * obsah z databáze nese `data-fancybox` a filtr `the_content` ho při
 * renderu přejmenovává právě na tenhle atribut (DB se nemění).
 */
const ITEM = '.zoomable, [data-za-lightbox]';
/*
 * VIDEA: dřív jen `.pmain[data-za-embed]` (jedna dlaždice na detailu
 * programu). Teď kterýkoli prvek s adresou přehrávače, protože spouštěčů
 * je na webu pět a v obsahu článků další.
 */
const VIDEO = '[data-za-embed]';

/**
 * Obsluha jednoho překryvu. Stav (index, otevřeno/zavřeno) žije tady,
 * ne v DOM: jediné, co se do DOM zapisuje, je třída `on` a obsah
 * překryvu.
 *
 * @param {HTMLElement} box Kořen překryvu.
 * @param {HTMLElement[]} items Prvky, které se dají zvětšit.
 * @param {HTMLElement[]} videos Spouštěče videa (`data-za-embed`).
 */
function setup(box, items, videos) {
	const big = box.querySelector('img');
	const count = box.querySelector('.lbcount');
	const holder = box.querySelector('.lbvideo');
	const close = box.querySelector('.lbclose');
	const prev = box.querySelector('.lbprev');
	const next = box.querySelector('.lbnext');

	let index = 0;
	let last = null;

	/** Zdroje se čtou až při otevření: `currentSrc` zná až vybraná varianta ze `srcset`. */
	function sources() {
		return items.map((el) => {
			const img = el.querySelector('img');
			return {
				src: el.getAttribute('href') || (img && (img.currentSrc || img.src)) || '',
				alt: (img && img.getAttribute('alt')) || '',
				/*
				 * `srcset` PRO PŘEKRYV (audit obrázků K4).
				 *
				 * Do téhle dávky bral překryv výhradně `href`, tedy ORIGINÁL:
				 * na mobilu se do okna 359×239 stahoval soubor 1920 px
				 * a místy přes 1 MB. Přitom mechanismus už existuje — galerie
				 * programu vypisuje `data-za-srcset` / `data-za-sizes`
				 * (`template-parts/program-2026.php`) a nikdo je nečetl.
				 *
				 * Přednost má atribut na odkazu (nese varianty pro velké
				 * zobrazení), pak `srcset` samotné miniatury. Když není ani
				 * jedno, zůstává `href` jako dosud — překryv se tím nemůže
				 * rozbít tam, kde varianty nejsou.
				 */
				srcset: el.getAttribute('data-za-srcset') || (img && img.getAttribute('srcset')) || '',
				sizes: el.getAttribute('data-za-sizes') || '',
			};
		});
	}

	function show(k) {
		const list = sources();
		if (!list.length) return;
		index = (k + list.length) % list.length;
		/*
		 * `srcset` se nastavuje PŘED `src`, aby prohlížeč vybíral z variant
		 * rovnou a nezačal stahovat originál, který by pak zahodil.
		 * Prázdná hodnota se ODSTRAŇUJE, ne nastavuje na "" — zbytek po
		 * předchozí fotce by jinak vybral variantu cizího obrázku.
		 */
		if (list[index].srcset) {
			big.setAttribute('srcset', list[index].srcset);
			if (list[index].sizes) {
				big.setAttribute('sizes', list[index].sizes);
			} else {
				/* Překryv kreslí přes celé okno; bez `sizes` by prohlížeč
				   počítal se šířkou rodiče a vybral zbytečně malou variantu. */
				big.setAttribute('sizes', '100vw');
			}
		} else {
			big.removeAttribute('srcset');
			big.removeAttribute('sizes');
		}
		big.src = list[index].src;
		big.alt = list[index].alt;
		if (count) count.textContent = `${index + 1} / ${list.length}`;
		if (prev) prev.hidden = list.length < 2;
		if (next) next.hidden = list.length < 2;
	}

	function open(k, opener) {
		last = opener || null;
		if (holder) {
			holder.innerHTML = '';
			holder.hidden = true;
		}
		big.hidden = false;
		box.classList.remove('video');
		show(k);
		box.classList.add('on');
		document.body.classList.add('lbox-open');
		if (close) close.focus();
	}

	function openVideo(opener) {
		if (!holder) return;
		last = opener || null;
		const src = opener.getAttribute('data-za-embed');
		if (!src) return;

		big.hidden = true;
		holder.hidden = false;
		/*
		 * `<iframe>` se skládá až tady a při zavření se maže. Tím se
		 * přehrávání zastaví, aniž by se do něj muselo sahat API —
		 * a hlavně se nic nenačítá, dokud na to nikdo neklikne.
		 */
		const frame = document.createElement('iframe');
		frame.setAttribute('src', src);
		frame.setAttribute('title', opener.getAttribute('aria-label') || '');
		frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen');
		frame.setAttribute('allowfullscreen', 'allowfullscreen');
		/*
		 * CHYBA 153 (hotfix dev.16.5, bod A). YouTube občas přehrávač
		 * odmítne hlásit „Chyba konfigurace přehrávače videí" (debug
		 * `embedder.identity.missing.referrer`), když iframu chybí
		 * hlavička `Referer` — třeba když prohlížeč pošle žádnou/
		 * omezenou referrer politiku. `strict-origin-when-cross-origin`
		 * pošle aspoň origin (ne celou cestu), což YouTube k ověření
		 * embedujícího webu stačí. Doporučení YouTube i zavedená praxe.
		 */
		frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
		holder.appendChild(frame);

		box.classList.add('on', 'video');
		document.body.classList.add('lbox-open');
		if (close) close.focus();
	}

	function hide() {
		box.classList.remove('on', 'video');
		document.body.classList.remove('lbox-open');
		if (holder) {
			holder.innerHTML = '';
			holder.hidden = true;
		}
		big.hidden = false;
		/* Fokus zpátky na prvek, ze kterého se otevřelo — jinak spadne na `<body>`. */
		if (last && typeof last.focus === 'function') last.focus();
	}

	items.forEach((el, k) => {
		el.addEventListener('click', (e) => {
			e.preventDefault();
			open(k, el);
		});
	});

	for (const v of videos) {
		v.addEventListener('click', (e) => {
			e.preventDefault();
			openVideo(v);
		});
	}

	if (close) close.addEventListener('click', hide);
	if (prev) prev.addEventListener('click', () => show(index - 1));
	if (next) next.addEventListener('click', () => show(index + 1));

	box.addEventListener('click', (e) => {
		if (e.target === box) hide();
	});

	/*
	 * FOKUSOVÁ SMYČKA.
	 *
	 * Překryv je `aria-modal`, takže za jeho hranici nemá Tab jak
	 * odejít — jenže sonda `verification/klavesnice.js` naměřila, že
	 * ze pěti Tabů uvnitř tři fokus vyvedly na stránku pod překryvem.
	 * Čtečka pak čte obsah, který je pro ni schovaný, a uživatel
	 * klávesnice se „ztratí" na stránce, kterou nevidí.
	 *
	 * Ovladatelné prvky se hledají PŘI KAŽDÉM Tabu, ne jednou při
	 * otevření: u videa se `.lbprev` a `.lbnext` schovávají, takže
	 * seznam z okamžiku otevření by posílal fokus na skrytý prvek.
	 */
	const OVLADATELNE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
	function smycka(e) {
		const prvky = [...box.querySelectorAll(OVLADATELNE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
		if (!prvky.length) return;
		const prvni = prvky[0];
		const posledni = prvky[prvky.length - 1];
		const aktivni = document.activeElement;
		// Fokus mimo překryv (např. po otevření) patří zpátky dovnitř.
		if (!box.contains(aktivni)) {
			e.preventDefault();
			prvni.focus();
			return;
		}
		if (e.shiftKey && aktivni === prvni) {
			e.preventDefault();
			posledni.focus();
		} else if (!e.shiftKey && aktivni === posledni) {
			e.preventDefault();
			prvni.focus();
		}
	}

	document.addEventListener('keydown', (e) => {
		if (!box.classList.contains('on')) return;
		if (e.key === 'Tab') { smycka(e); return; }
		if (e.key === 'Escape') hide();
		if (box.classList.contains('video')) return;
		if (e.key === 'ArrowLeft') show(index - 1);
		if (e.key === 'ArrowRight') show(index + 1);
	});
}

/**
 * Spustí modul. Vrací počet obsloužených překryvů (0 nebo 1) — kontrakt
 * je stejný jako u ostatních modulů, aby se dalo doložit, kolik obsluh
 * nad stránkou běží.
 *
 * @returns {number}
 */
export function init() {
	const box = document.querySelector(ROOT);
	if (!box) return 0;

	const items = Array.prototype.slice.call(document.querySelectorAll(ITEM));
	const videos = Array.prototype.slice.call(document.querySelectorAll(VIDEO));
	if (!items.length && !videos.length) return 0;

	setup(box, items, videos);
	return 1;
}
